import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { collection, getDocs, query, where, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import useAuthStore from '../store/authStore';
import { usePermissao } from '../hooks/usePermissao';

const TIPO_CORES = {
  info:    { bg: '#E6F1FB', cor: '#185FA5', border: '#378ADD', icon: 'ℹ️' },
  aviso:   { bg: '#FAEEDA', cor: '#633806', border: '#EF9F27', icon: '⚠️' },
  urgente: { bg: '#FCEBEB', cor: '#791F1F', border: '#B51013', icon: '🚨' },
  sucesso: { bg: '#E1F5EE', cor: '#085041', border: '#1D9E75', icon: '✅' },
};

export function BannerAnuncio() {
  const usuario = useAuthStore((s) => s.usuario);
  const { pode } = usePermissao();
  const isMobile = useIsMobile();
  const [anuncios, setAnuncios] = useState([]);
  const [fechados, setFechados] = useState(() => {
    try { return JSON.parse(localStorage.getItem('anuncios_fechados') || '[]'); } catch { return []; }
  });
  const [criando, setCriando] = useState(false);
  const [form, setForm] = useState({ titulo: '', mensagem: '', tipo: 'info', expiresHoras: 24 });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!usuario) return;
    const agora = new Date().toISOString();
    getDocs(query(collection(db, 'anuncios'), where('ativo', '==', true), orderBy('criadoEm', 'desc'))).then(snap => {
      const lista = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(a => !a.expiresAt || a.expiresAt > agora)
        .filter(a => !fechados.includes(a.id));
      setAnuncios(lista);
    }).catch(() => {});
  }, [usuario]);

  const fechar = (id) => {
    const novos = [...fechados, id];
    setFechados(novos);
    localStorage.setItem('anuncios_fechados', JSON.stringify(novos));
    setAnuncios(prev => prev.filter(a => a.id !== id));
  };

  const criar = async () => {
    if (!form.titulo || !form.mensagem) return;
    setSalvando(true);
    try {
      const expiresAt = form.expiresHoras > 0
        ? new Date(Date.now() + form.expiresHoras * 3600000).toISOString()
        : null;
      await addDoc(collection(db, 'anuncios'), {
        titulo: form.titulo,
        mensagem: form.mensagem,
        tipo: form.tipo,
        ativo: true,
        expiresAt,
        criadoPor: usuario.uid,
        criadoEm: new Date().toISOString(),
      });
      setForm({ titulo: '', mensagem: '', tipo: 'info', expiresHoras: 24 });
      setCriando(false);
    } catch (e) { console.error(e); }
    setSalvando(false);
  };

  return (
    <>
      {/* Banners ativos */}
      {anuncios.map(a => {
        const cfg = TIPO_CORES[a.tipo] || TIPO_CORES.info;
        return (
          <div key={a.id} style={{
            background: cfg.bg, borderBottom: `2px solid ${cfg.border}`,
            padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '13px',
          }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>{cfg.icon}</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontWeight: '700', color: cfg.cor }}>{a.titulo}: </span>
              <span style={{ color: cfg.cor }}>{a.mensagem}</span>
            </div>
            <span onClick={() => fechar(a.id)} style={{ cursor: 'pointer', color: cfg.cor, opacity: 0.6, fontSize: '16px', flexShrink: 0 }}>✕</span>
          </div>
        );
      })}

      {/* Painel de criação (só para nível 4+, oculto no mobile para não sobrepor bottom nav) */}
      {pode('configurarNotificacoes') && !isMobile && (
        <div style={{ position: 'fixed', bottom: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 400 }}>
          {!criando ? (
            <button onClick={() => setCriando(true)} style={{ padding: '8px 16px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>📢</span> Enviar anúncio
            </button>
          ) : (
            <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', boxShadow: '0 8px 30px rgba(0,0,0,0.2)', width: '360px', border: '1px solid #e5e5e5' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600' }}>📢 Novo anúncio</div>
                <span onClick={() => setCriando(false)} style={{ cursor: 'pointer', color: '#aaa', fontSize: '16px' }}>✕</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <input value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} placeholder="Título *" style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', outline: 'none' }} />
                <textarea value={form.mensagem} onChange={e => setForm(p => ({ ...p, mensagem: e.target.value }))} placeholder="Mensagem *" style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', outline: 'none', resize: 'none', height: '60px' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))} style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '7px 8px', fontSize: '12px', outline: 'none' }}>
                    <option value="info">ℹ️ Informativo</option>
                    <option value="aviso">⚠️ Aviso</option>
                    <option value="urgente">🚨 Urgente</option>
                    <option value="sucesso">✅ Boas notícias</option>
                  </select>
                  <select value={form.expiresHoras} onChange={e => setForm(p => ({ ...p, expiresHoras: Number(e.target.value) }))} style={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '7px 8px', fontSize: '12px', outline: 'none' }}>
                    <option value={1}>Expira em 1h</option>
                    <option value={8}>Expira em 8h</option>
                    <option value={24}>Expira em 24h</option>
                    <option value={72}>Expira em 3 dias</option>
                    <option value={0}>Sem expiração</option>
                  </select>
                </div>
                <button onClick={criar} disabled={salvando || !form.titulo || !form.mensagem} style={{ padding: '8px', background: salvando || !form.titulo ? '#ccc' : '#B51013', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: salvando || !form.titulo ? 'not-allowed' : 'pointer' }}>
                  {salvando ? 'Enviando...' : 'Enviar para todos'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
